/* eslint-disable no-undef */
'use client';

import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ColorField } from '@prismicio/client';
import { omit } from 'lodash';
import { MAP_PIN_SVG } from '@/constants';
import { useGoogleMapsJsReady } from '@/hooks';
import styles from './Map.module.scss';
import type {
  CenteredMode,
  CreateMarkerOptions,
  ILocation,
  IMarkerStyle,
  MapStyleTokens,
} from './map.types';
import {
  buildMapStyles,
  escapeHtml,
  HIDE_INTERESTING_POINTS_STYLES,
} from './map.styles';
import { MAP_PALETTES, resolveMapPalette } from './map.palettes';

const GOOGLE_MAPS_SCRIPT_URL = process.env.NEXT_PUBLIC_GOOGLE_MAP_API ?? '';
const REVERSE_PROXY_LINK = process.env.NEXT_PUBLIC_REVERSE_PROXY_LINK ?? '';

interface UseGoogleMapOptions {
  locations: ILocation[];
  mapStyleTokens: MapStyleTokens;
  hideInterestingPoints: boolean;
  centered: CenteredMode;
  zoom: number;
  context: string;
  openInfo: boolean;
  showTitle: boolean;
  narrowMapTooltip: boolean;
  pinColor?: ColorField;
  defaultMarker?: string;
  markerStyle?: IMarkerStyle;
  actionClickMarker?: (_marker: google.maps.Marker) => void;
}

const getCssOffsetValue = (
  container: HTMLElement | null,
  varName: string,
): number => {
  if (!container) return 0;
  const raw = getComputedStyle(container).getPropertyValue(varName).trim();
  const parsed = Number.parseInt(raw, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const useGoogleMap = (
  containerRef: RefObject<HTMLElement | null>,
  options: UseGoogleMapOptions,
) => {
  const {
    locations,
    mapStyleTokens,
    hideInterestingPoints,
    centered,
    zoom,
    context,
    openInfo,
    showTitle,
    narrowMapTooltip,
    pinColor,
    defaultMarker,
    markerStyle,
    actionClickMarker,
  } = options;

  const mapsJsReady = useGoogleMapsJsReady();
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const actionClickMarkerRef =
    useRef<UseGoogleMapOptions['actionClickMarker']>(actionClickMarker);
  actionClickMarkerRef.current = actionClickMarker;

  const appliedStyles = useMemo(() => {
    const palette = MAP_PALETTES[mapStyleTokens];
    const modeStyles = palette ? buildMapStyles(palette) : undefined;
    if (modeStyles && hideInterestingPoints) {
      return [...modeStyles, ...HIDE_INTERESTING_POINTS_STYLES];
    }
    return (
      modeStyles ||
      (hideInterestingPoints ? HIDE_INTERESTING_POINTS_STYLES : undefined)
    );
  }, [mapStyleTokens, hideInterestingPoints]);

  const resolvedPalette = useMemo(
    () => resolveMapPalette(mapStyleTokens),
    [mapStyleTokens],
  );

  // eslint-disable-next-line max-len
  /** Kept in a ref so `createMarker` stays referentially stable when parents pass inline objects (e.g. `markerStyle={{...}}` each render). Otherwise the map effect re-runs and tears down markers/InfoWindow on every React update (e.g. marker click → `setState`). */
  const markerVisualRef = useRef({
    resolvedPalette,
    markerStyle,
    pinColor,
    showTitle,
    openInfo,
  });
  markerVisualRef.current = {
    resolvedPalette,
    markerStyle,
    pinColor,
    showTitle,
    openInfo,
  };

  const createMarker = useCallback(
    (opts: CreateMarkerOptions): google.maps.Marker => {
      const {
        lat,
        lng,
        label,
        map,
        bounds,
        pin,
        locationImage,
        googleMapUrl,
        narrowTooltip = false,
      } = opts;

      const mv = markerVisualRef.current;
      const tt = mv.resolvedPalette;
      const labelColor = mv.markerStyle?.titleColor;
      const fillColor = mv.markerStyle?.defaultMarkerColor as string;
      const maxWidth = narrowTooltip ? '135px' : '180px';
      const imageUrl =
        locationImage ?? `${REVERSE_PROXY_LINK}/images/product-placeholder.svg`;
      const mapUrl = googleMapUrl ?? `${REVERSE_PROXY_LINK}/`;

      const basePin = pin
        ? {
            scaledSize: new google.maps.Size(40, 50),
            url: pin,
            labelOrigin: new google.maps.Point(15, 66),
          }
        : {
            anchor: new google.maps.Point(15, 30),
            labelOrigin: new google.maps.Point(15, 43),
            ...(mv.pinColor
              ? { ...omit(MAP_PIN_SVG, 'fillColor'), fillColor: mv.pinColor }
              : { ...MAP_PIN_SVG, fillColor }),
          };

      const marker = new google.maps.Marker({
        icon: basePin,
        map,
        position: { lat, lng },
        title: mv.showTitle ? label : '',
        label: mv.showTitle
          ? {
              text: label,
              color: labelColor || tt.markerTitle,
              fontSize: '12px',
              fontWeight: 'bold',
            }
          : '',
      });

      marker.addListener('click', () => {
        actionClickMarkerRef.current?.(marker);

        const { openInfo: shouldOpenInfo, resolvedPalette: palette } =
          markerVisualRef.current;
        if (!shouldOpenInfo) return;

        infoWindowRef.current?.close();

        const safeLabel = escapeHtml(label);

        const contentHTML = `
          <div class="${styles.mapInfoWindowBubble}"
               style="--map-iw-peak-bg: ${palette.tooltipBackground};
                      --map-iw-peak-border: ${palette.tooltipBorder};
                      --map-iw-bg: ${palette.tooltipBackground};
                      --map-iw-text: ${palette.tooltipText};
                      --map-iw-border: ${palette.tooltipBorder};
                      --map-iw-radius: ${palette.tooltipBorderRadius};
                      --map-iw-max-w: ${maxWidth};">
            <div class="${styles.mapInfoWindowHeader}">
              <h3 class="${
                styles.mapInfoWindowTitle
              }" title="${safeLabel}">${safeLabel}</h3>
              <button type="button" class="${
                styles.mapInfoWindowClose
              }" data-map-info-close="true">&#10006;</button>
            </div>
            <a class="${styles.mapInfoWindowMedia}"
               href="${escapeHtml(mapUrl)}"
               target="_blank" rel="noopener noreferrer">
              <img class="${styles.mapInfoWindowImage}"
                   src="${escapeHtml(imageUrl)}"
                   alt="${safeLabel}" />
            </a>
            <div class="${styles.mapInfoWindowPeak}" aria-hidden="true"></div>
          </div>
        `;

        const info = new google.maps.InfoWindow({ content: contentHTML });
        infoWindowRef.current = info;
        info.open(map, marker);

        google.maps.event.addListenerOnce(info, 'domready', () => {
          // Content mounts under .gm-style-iw-c (often outside `containerRef`), not inside the React figure.
          document
            .querySelector<HTMLButtonElement>(
              '.gm-style-iw-c [data-map-info-close]',
            )
            ?.addEventListener('click', () => info.close());
        });
      });

      try {
        bounds?.extend(marker.getPosition() as google.maps.LatLng);
      } catch {
        // Position may not be resolved yet
      }

      return marker;
    },
    [containerRef],
  );

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach(m => {
      google.maps.event.clearInstanceListeners(m);
      m.setMap(null);
    });
    markersRef.current = [];
    infoWindowRef.current?.close();
    infoWindowRef.current = null;
  }, []);

  useEffect(() => {
    if (!locations.length || !mapsJsReady || !GOOGLE_MAPS_SCRIPT_URL) return;

    const container = containerRef.current;
    if (!container) return;

    const { lat, lng } = locations[0];
    const isSingle = locations.length === 1;

    const mapOptions: google.maps.MapOptions = {
      disableDefaultUI: true,
      zoom,
      center: { lat, lng },
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      colorScheme:
        google.maps.ColorScheme?.LIGHT ??
        ('LIGHT' as google.maps.MapOptions['colorScheme']),
      ...(appliedStyles && { styles: appliedStyles }),
      ...(google.maps.RenderingType
        ? { renderingType: google.maps.RenderingType.RASTER }
        : {}),
    };

    const map = new google.maps.Map(container, mapOptions);

    if (appliedStyles?.length) {
      const styleOpts: google.maps.MapOptions = {
        styles: appliedStyles,
        colorScheme:
          google.maps.ColorScheme?.LIGHT ??
          ('LIGHT' as google.maps.MapOptions['colorScheme']),
      };
      map.setOptions(styleOpts);
      google.maps.event.addListenerOnce(map, 'idle', () =>
        map.setOptions(styleOpts),
      );
    }

    clearMarkers();
    const bounds = new google.maps.LatLngBounds();

    locations.forEach(loc => {
      const marker = createMarker({
        lat: loc.lat,
        lng: loc.lng,
        label: loc.label,
        map,
        bounds: isSingle ? undefined : bounds,
        pin: defaultMarker ?? loc.pin,
        locationImage: loc.photo,
        googleMapUrl: loc.google_map_url,
        narrowTooltip: narrowMapTooltip,
      });
      markersRef.current.push(marker);
    });

    const applyViewport = () => {
      if (isSingle) {
        const x = getCssOffsetValue(container, '--map-pan-x');
        const y = getCssOffsetValue(container, '--map-pan-y');
        const pos = new google.maps.LatLng(lat, lng);
        map.panTo(pos);
        map.panBy(x, y);

        google.maps.event.addListenerOnce(map, 'idle', () => {
          if (!map.getBounds()?.contains(pos)) map.panTo(pos);
        });
      } else {
        const padding = {
          top: 100,
          right: 50,
          bottom: 50,
          left: getCssOffsetValue(container, '--map-fitbounds-left'),
        };
        map.fitBounds(bounds, centered === 'center' ? undefined : padding);
      }
    };

    const initialTimeout = window.setTimeout(applyViewport, 200);

    let resizeRaf = 0;
    const handleResize = () => {
      if (resizeRaf) window.cancelAnimationFrame(resizeRaf);
      resizeRaf = window.requestAnimationFrame(applyViewport);
    };
    window.addEventListener('resize', handleResize);

    const fixA11yLabels = () => {
      container
        .querySelectorAll<HTMLDivElement>(
          'div[role="button"][title=""], div[role="button"]:not([aria-label])',
        )
        .forEach(el => {
          if (!el.getAttribute('aria-label'))
            el.setAttribute('aria-label', 'Map control');
          if (el.getAttribute('title') === '') el.removeAttribute('title');
        });
      container
        .querySelectorAll<HTMLDivElement>(
          'div[role="region"][aria-roledescription="map"][aria-label="Map"]',
        )
        .forEach(el => el.setAttribute('aria-label', context));
    };

    const a11yTimeout = window.setTimeout(fixA11yLabels, 500);
    const observer = new MutationObserver(fixA11yLabels);
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      window.clearTimeout(initialTimeout);
      window.clearTimeout(a11yTimeout);
      if (resizeRaf) window.cancelAnimationFrame(resizeRaf);
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
      clearMarkers();
    };
  }, [
    appliedStyles,
    centered,
    clearMarkers,
    containerRef,
    context,
    createMarker,
    defaultMarker,
    locations,
    mapsJsReady,
    narrowMapTooltip,
    zoom,
  ]);
};
